import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Copy, Check, Loader2, Plus, Pencil, X } from "lucide-react";
import { buildScheduleReviewLink, scheduleModeLabel } from "@/lib/scheduleShare";

interface ShareScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productionId: string;
}

interface ShareRow {
  id: string;
  label: string;
  access_token: string;
  mode: string;
  allow_feedback: boolean;
  created_at: string;
  last_viewed_at: string | null;
}

const formatWhen = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "Not viewed yet";

/**
 * Producer-only dialog to share a production's schedule with a client via a
 * secret read-only link. Lists active links, creates a new one (label + feedback
 * toggle), and revokes. The client never gets a session — the link alone grants
 * read-only access.
 */
export default function ShareScheduleDialog({
  open,
  onOpenChange,
  productionId,
}: ShareScheduleDialogProps) {
  const queryClient = useQueryClient();
  const queryKey = ["schedule-shares", productionId];

  const { data: shares = [], isLoading } = useQuery({
    queryKey,
    enabled: open,
    queryFn: async (): Promise<ShareRow[]> => {
      const { data, error } = await supabase
        .from("schedule_shares")
        .select("id, label, access_token, mode, allow_feedback, created_at, last_viewed_at")
        .eq("briefing_id", productionId)
        .is("revoked_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as ShareRow[];
    },
  });

  const [label, setLabel] = useState("");
  const [allowFeedback, setAllowFeedback] = useState(true);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ShareRow | null>(null);
  // Inline rename of an existing link's client name.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setLabel("");
      setAllowFeedback(true);
      setCreatedLink(null);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("create-schedule-share", {
        body: {
          briefing_id: productionId,
          label: label.trim(),
          allow_feedback: allowFeedback,
        },
      });
      if (error) throw new Error(error.message || "Could not create link");
      if (!data?.success || !data?.link) throw new Error(data?.error || "Could not create link");
      return data.link as string;
    },
    onSuccess: (link) => {
      setCreatedLink(link);
      queryClient.invalidateQueries({ queryKey });
      toast.success("Share link created");
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Could not create link");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke("revoke-schedule-share", {
        body: { share_id: id },
      });
      if (error) throw new Error(error.message || "Could not revoke");
      if (!data?.success) throw new Error(data?.error || "Could not revoke");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Link revoked");
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Could not revoke");
    },
  });

  // Toggle feedback on/off for an EXISTING link (producer owns the row via RLS).
  const toggleFeedbackMutation = useMutation({
    mutationFn: async ({ id, allow }: { id: string; allow: boolean }) => {
      const { error } = await supabase
        .from("schedule_shares")
        .update({ allow_feedback: allow })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: () => toast.error("Could not update feedback setting"),
  });

  // Rename an existing link's client label.
  const renameMutation = useMutation({
    mutationFn: async ({ id, newLabel }: { id: string; newLabel: string }) => {
      const { error } = await supabase
        .from("schedule_shares")
        .update({ label: newLabel })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey });
      toast.success("Name updated");
    },
    onError: () => toast.error("Could not rename link"),
  });

  const saveRename = (id: string) => {
    const trimmed = editLabel.trim();
    if (!trimmed) return;
    renameMutation.mutate({ id, newLabel: trimmed });
  };

  const copyLink = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select and copy the link manually");
    }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : undefined;
  const canSubmit = label.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Share with client</DialogTitle>
          <DialogDescription>
            Create a secret, read-only link to this schedule. The client can filter and — if allowed
            — leave feedback, but can't change anything.
          </DialogDescription>
        </DialogHeader>

        {/* New link form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMutation.mutate();
          }}
          className="space-y-4 border-b border-border pb-5"
        >
          <div className="space-y-2">
            <Label htmlFor="share-label">Name / label</Label>
            <Input
              id="share-label"
              placeholder="e.g. Festival Helden organisation"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={80}
              required
            />
          </div>

          {/* The mode shown to the client reflects the production's real
              publication status automatically — no choice needed here. */}
          <p className="text-xs text-muted-foreground">
            The client sees whether the schedule is published or still unpublished, based on the
            production's status.
          </p>

          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <Label htmlFor="share-feedback" className="cursor-pointer">
              Allow feedback
            </Label>
            <Switch
              id="share-feedback"
              checked={allowFeedback}
              onCheckedChange={setAllowFeedback}
            />
          </div>

          {createdLink ? (
            <div className="space-y-2 rounded-md bg-muted/50 p-3">
              <p className="text-sm font-medium">Share this link with the client:</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={createdLink} onFocus={(e) => e.currentTarget.select()} />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyLink(createdLink, "new")}
                >
                  {copiedId === "new" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Button type="submit" disabled={!canSubmit || createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create link
                </>
              )}
            </Button>
          )}
        </form>

        {/* Existing links */}
        <div>
          <p className="mb-2 text-sm font-medium">Active links</p>
          {isLoading ? (
            <div className="py-3 text-sm text-muted-foreground">Loading…</div>
          ) : shares.length === 0 ? (
            <div className="py-3 text-sm text-muted-foreground">No active links yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {shares.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    {editingId === s.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          maxLength={80}
                          autoFocus
                          className="h-8"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveRename(s.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Save"
                          onClick={() => saveRename(s.id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Cancel"
                          onClick={() => setEditingId(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{s.label}</span>
                        <button
                          type="button"
                          title="Rename"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setEditingId(s.id);
                            setEditLabel(s.label);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <Badge variant={s.mode === "published" ? "default" : "secondary"}>
                          {scheduleModeLabel(s.mode)}
                        </Badge>
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-2">
                      <Switch
                        id={`feedback-${s.id}`}
                        checked={s.allow_feedback}
                        onCheckedChange={(allow) =>
                          toggleFeedbackMutation.mutate({ id: s.id, allow })
                        }
                        className="scale-90"
                      />
                      <Label htmlFor={`feedback-${s.id}`} className="cursor-pointer text-xs">
                        Feedback {s.allow_feedback ? "on" : "off"}
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        · viewed {formatWhen(s.last_viewed_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Copy link"
                      onClick={() =>
                        copyLink(buildScheduleReviewLink(s.access_token, origin), s.id)
                      }
                    >
                      {copiedId === s.id ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setRevokeTarget(s)}
                    >
                      Revoke
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Revoke confirmation */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke link?</AlertDialogTitle>
            <AlertDialogDescription>
              The link "{revokeTarget?.label}" will stop working. The client will no longer be able
              to view the schedule. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (revokeTarget) revokeMutation.mutate(revokeTarget.id);
                setRevokeTarget(null);
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
